/**
 * AuthContext — single source of truth for authenticated user state.
 *
 * Zitadel is the sole auth path (SSO re-architecture, Phase 5). On mount we
 * register the bearer-token provider, re-load any persisted Zitadel token, and
 * hydrate from GET /auth/me (sent with the bearer). No cookies, no CSRF, no
 * host↔tenant reconciliation — the tenant comes from the token.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { components } from "@/api/schema";

import { authFetch, setBearerProvider } from "./fetchClient";
import { AuthContext, type AuthContextValue, type State } from "./useAuth";
import { getFreshAccessToken, rehydrateZitadelToken, zitadelLogout } from "./zitadelClient";

// `/auth/me` returns the user's identity claims plus session-timing fields.
// We split them: identity goes into AuthUser (consumed via useAuth), timing
// fields turn into State.expires_at / idle_expires_at / server_time_offset
// (clock-skew-corrected timestamps the SPA can render countdowns against).
type MeResponse = components["schemas"]["_MeResponseSchema"];

const initialState: State = {
  status: "loading",
  user: null,
  expires_at: null,
  idle_expires_at: null,
  server_time_offset: 0,
};

function applyMe(data: MeResponse): State {
  const server_time_ms = new Date(data.server_time).getTime();
  const offset = server_time_ms - Date.now();
  const now_server = Date.now() + offset;
  const { expires_in, idle_expires_in, server_time, ...identity } = data;
  void server_time; // already consumed above
  return {
    status: "authenticated",
    user: identity,
    expires_at: expires_in === null ? null : now_server + expires_in * 1000,
    idle_expires_at: idle_expires_in === null ? null : now_server + idle_expires_in * 1000,
    server_time_offset: offset,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>(initialState);

  const refresh_me = useCallback(async () => {
    const res = await authFetch("/auth/me");
    if (!res.ok) {
      setState({ ...initialState, status: "unauthenticated" });
      return;
    }
    const data = (await res.json()) as MeResponse;
    setState(applyMe(data));
  }, []);

  // Initial hydration: attach a fresh bearer on every request (refreshes on
  // demand via the refresh token), re-load / silently renew a persisted token,
  // then read /auth/me.
  useEffect(() => {
    setBearerProvider(getFreshAccessToken);
    void rehydrateZitadelToken().then(() => refresh_me());
  }, [refresh_me]);

  const logout = useCallback(async () => {
    // Record the logout while the token is still valid (Zitadel then ends the
    // session). Best-effort — never block sign-out.
    await authFetch("/auth/logout-event", { method: "POST" }).catch(() => {});
    await zitadelLogout();
    setState({ ...initialState, status: "unauthenticated" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      logout,
      refresh_me,
      hasPermission: (permission) => state.user?.permissions.includes(permission) ?? false,
      hasRole: (role) => state.user?.roles.includes(role) ?? false,
    }),
    [state, logout, refresh_me],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
